import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchCategoriesApi } from 'api/categories-api';
import { fetchRecommendedProductVariantsApi } from 'api/product-variant-api';
import { fetchTemplateListApi } from 'api/templates-api';
import { fetchTypesApi } from 'api/types-api';
import toast from 'react-hot-toast';
import {
  fetchFilterLabelsApi,
  fetchFilterStatusesApi,
  fetchJourneyStagesApi,
  fetchPharmaciesApi,
  fetchPrescriptionsApi,
  fetchAmericanStatesApi,
  fetchStatesApi,
  fetchTypeApi,
  fetchUserStatesApi,
} from '../../api/filters-data-api';
import { searchProductVariantsApi } from '../../api/products-api';
import { fetchThirdPartyUsersApi, fetchUsersListApi } from '../../api/users-api';
import { getErrorString, isNull } from '../../utils';
import { formatOptionsForSelect, getQueryString } from '../../utils/filters-util';
import { fetchStatusesApi } from 'api/statuses-api';
import { fetchQuizAttemptQuizConfigApi } from 'api/quiz-attempts-api';

const initialState = {
  leadStatuses: null,
  caseStatuses: null,
  trackingStatuses: null,
  trackingStates: null,
  subscriptionStatuses: null,
  ticketStatuses: null,
  appointmentStatuses: null,
  followUpStatuses: null,
  productStatuses: null,
  productVariantStatuses: null,
  quizAttemptStatuses: null,
  labOrderStatuses: null,
  products: {},
  internalProducts: {},
  ticketTypes: null,
  orderTypes: null,
  voucherTypes: null,
  contactTypes: null,
  caseCommunicationTypes: null,
  appointmentsTypes: null,
  followUpTypes: null,
  productTypes: null,
  labOrderTypes: null,
  discountUnitTypes: null,
  couponTypes: null,
  leaveTypes: null,
  pharmacyTypes: null,
  trackingCarriers: null,
  processors: null,
  orderStatuses: null,
  states: null,
  americanStates: null,
  pharmacies: {},
  chargeStatuses: null,
  intakeStatuses: null,
  scheduledJobStatuses: null,
  adminUsers: null,
  thirdPartyUsers: null,
  journeyStages: null,
  prescriptions: null,
  planTypes: null,
  productCategories: null,
  followupTemplates: null,
  agentSalesStatuses: null,
  recommendedProductVariants: null,
  creditStatuses: null,
  labReviewLabels: null,
  labReviewStatuses: null,
  userLabReviewStatuses: null,
  labReviewIndexStatuses: null,
  quizNames: null,
  quizSteps: null,
};

const defaultStatusParams = {
  sort: 'position asc',
};

export const fetchAgentSalesStatuses = createAsyncThunk('agent-sales/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.agentSalesStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'agentsale', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeadStatuses = createAsyncThunk('lead/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.leadStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'lead', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCaseStatuses = createAsyncThunk('case/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.caseStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'case', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchOrderStatuses = createAsyncThunk('order/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.orderStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'order', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTrackingStatuses = createAsyncThunk('tracking/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.trackingStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'PharmacyTracking', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTrackingStates = createAsyncThunk('tracking/states', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.trackingStates);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'TrackingState', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchSubscriptionStatuses = createAsyncThunk('subscription/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.subscriptionStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'subscription', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTicketStatuses = createAsyncThunk('ticket/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.ticketStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'ticket', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchAppointmentStatuses = createAsyncThunk('appointment/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.appointmentStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'appointment', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchFollowUpStatuses = createAsyncThunk('followup/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.followUpStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'FollowUp', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchProductStatuses = createAsyncThunk('product/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.productStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'product', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchProductVariantStatuses = createAsyncThunk('product-variant/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.productVariantStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'productVariant', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchQuizAttemptStatuses = createAsyncThunk('quiz-attempt/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.quizAttemptStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'QuizAttempt', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTicketTypes = createAsyncThunk('ticket/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.ticketTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'f[source_class][eq]': 'ticket', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchOrderTypes = createAsyncThunk('order/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.orderTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'f[source_class][eq]': 'order', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchVoucherTypes = createAsyncThunk('voucher/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.voucherTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'f[source_class][eq]': 'voucher', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchContactTypes = createAsyncThunk('contact/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.contactTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'f[source_class][eq]': 'contact', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCaseCommunicationTypes = createAsyncThunk('case-communication/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.caseCommunicationTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'CaseCommunication', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchAppointmentsTypes = createAsyncThunk('appointments/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.appointmentsTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'Appointment', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchProductTypes = createAsyncThunk('product/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.productTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'product', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLabOrderTypes = createAsyncThunk('labOrder/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.labOrderTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'LabOrder', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchDiscountUnitTypes = createAsyncThunk('discountUnit/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.discountUnitTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'DiscountUnit', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCouponTypes = createAsyncThunk('coupon/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.couponTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'PromotionCode', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLeaveTypes = createAsyncThunk('leave/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.leaveTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'AbsenceRequest', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPharmacyTypes = createAsyncThunk('pharmacy/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.pharmacyTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'Pharmacy', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchTrackingCarriers = createAsyncThunk('trackingCarrier/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.trackingCarriers);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'TrackingCarrier', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchProcessors = createAsyncThunk('processor/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.processors);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'f[source_class][eq]': 'processor', per_page: 100 };
    const res = await fetchTypeApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchProducts = createAsyncThunk('products/list', async ({ value = '' } = {}, { getState }) => {
  const search = value || '';
  const state = getState();
  const isEmpty = isNull(state.filtersData?.products?.[search] || null);
  if (!isEmpty) return;

  try {
    const updatedParamsObj = { per_page: search ? 50 : 200, variant_info: search };
    const res = await searchProductVariantsApi(updatedParamsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchInternalProducts = createAsyncThunk(
  'products/list/internal',
  async ({ value = '' } = {}, { getState }) => {
    const search = value || '';
    const state = getState();
    const isEmpty = isNull(state.filtersData?.internalProducts?.[search] || null);
    if (!isEmpty) return;

    try {
      const updatedParamsObj = { per_page: search ? 50 : 200, variant_info: search, product_type: 'internal' };
      const res = await searchProductVariantsApi(updatedParamsObj);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
    }
  },
);

export const fetchJourneyStages = createAsyncThunk('journey-stages/list', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.journeyStages);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 100 };
    const res = await fetchJourneyStagesApi(getQueryString(paramsObj));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPrescriptions = createAsyncThunk('prescriptions/list', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.prescriptions);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 100 };
    const res = await fetchPrescriptionsApi(getQueryString(paramsObj));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchStates = createAsyncThunk('states/list', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.states);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 200 };
    const res = await fetchStatesApi(getQueryString(paramsObj));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchAmericanStates = createAsyncThunk('states/american/list', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.americanStates);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 500 };
    const res = await fetchAmericanStatesApi(getQueryString(paramsObj));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchUserStates = createAsyncThunk('states/users/list', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.states);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 200 };
    const res = await fetchUserStatesApi(getQueryString(paramsObj));
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPharmacies = createAsyncThunk(
  'pharmacy/all',
  async ({ value = '', per_page = 100 } = {}, { getState }) => {
    const search = value || '';
    const state = getState();
    const isEmpty = isNull(state.filtersData?.pharmacies?.[search] || null);
    if (!isEmpty) return;

    try {
      const paramsObj = { per_page, 'q[name_cont]': search };
      const res = await fetchPharmaciesApi(getQueryString(paramsObj), {});
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
    }
  },
);

export const fetchChargeStatuses = createAsyncThunk('charge/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.chargeStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'charge', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchIntakeStatuses = createAsyncThunk('intake/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.intakeStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'intake', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchScheduledJobsStatuses = createAsyncThunk('scheduledJobs/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.scheduledJobStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'ScheduleJob', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLabOrderStatuses = createAsyncThunk('labOrder/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.labOrderStatuses);
  if (!isEmpty) return;

  try {
    const paramsObj = { ...defaultStatusParams, 's[statusable_type]': 'LabOrder', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchAdminUsers = createAsyncThunk('admin/users', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.adminUsers);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 200 };
    const res = await fetchUsersListApi('', paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchThirdPartyUsers = createAsyncThunk('thirdParty/users', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.thirdPartyUsers);
  if (!isEmpty) return;

  try {
    const paramsObj = { per_page: 200 };
    const res = await fetchThirdPartyUsersApi('', paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchPlanTypes = createAsyncThunk('plan/types', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.planTypes);
  if (!isEmpty) return;

  try {
    const paramsObj = { 's[source_class]': 'Plan', per_page: 200 };
    const res = await fetchTypesApi(paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCategoryTypes = createAsyncThunk('categories', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.productCategories);
  if (!isEmpty) return;

  try {
    const paramsObj = { 'q[category_type_eq]': 'Product', per_page: 200 };
    const res = await fetchCategoriesApi(paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchFollowupTemplates = createAsyncThunk('followup-templates', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.followupTemplates);
  if (!isEmpty) return;

  try {
    const paramsObj = { class: 'EmailTemplate', 'q[s]': 'id desc', 'q[action_cont]': 'followup_day_', per_page: 200 };
    const res = await fetchTemplateListApi(paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchRecommendedProductVariants = createAsyncThunk('product-variants/list', async (_, { getState }) => {
  try {
    const state = getState();
    const isEmpty = isNull(state.filtersData.recommendedProductVariants);
    if (!isEmpty) return;

    const payload = { per_page: 200 };
    const res = await fetchRecommendedProductVariantsApi(payload);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchCreditStatuses = createAsyncThunk('credit/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.agentSalesStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { ...defaultStatusParams, 'f[statusable_type][eq]': 'credittransaction', per_page: 100 };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLabReviewLabels = createAsyncThunk('lab-reviews/labels', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.labReviewLabels);
  if (!isEmpty) return;
  try {
    const paramsObj = { 'q[key_eq]': 'lab_test_uploads' };
    const res = await fetchFilterLabelsApi(paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLabReviewIndexStatuses = createAsyncThunk('lab-review-index/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.labReviewIndexStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { ...defaultStatusParams, 's[status_type]': 'LabReview' };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchLabReviewStatuses = createAsyncThunk('lab-reviews/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.labReviewStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { ...defaultStatusParams, 's[status_type]': 'LabReviewTest' };
    const res = await fetchFilterStatusesApi(getQueryString(paramsObj), {});
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchUserLabReviewStatuses = createAsyncThunk('uer-lab-reviews/statuses', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.userLabReviewStatuses);
  if (!isEmpty) return;
  try {
    const paramsObj = { 's[status_type]': 'LabReviewTest' };
    const res = await fetchStatusesApi(paramsObj);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

export const fetchQuizSteps = createAsyncThunk('quiz/steps', async (_, { getState }) => {
  const state = getState();
  const isEmpty = isNull(state.filtersData.quizSteps);
  if (!isEmpty) return;
  try {
    const res = await fetchQuizAttemptQuizConfigApi();
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
  }
});

const FiltersDataSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setProductCategories: (state, { payload }) => {
      state.productCategories = payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchLeadStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.leadStatuses = action?.payload?.data;
    });
    builder.addCase(fetchCaseStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.caseStatuses = action?.payload?.data;
    });
    builder.addCase(fetchOrderStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.orderStatuses = action?.payload?.data;
    });
    builder.addCase(fetchSubscriptionStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.subscriptionStatuses = action?.payload?.data;
    });
    builder.addCase(fetchTrackingStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.trackingStatuses = action?.payload?.data;
    });
    builder.addCase(fetchTrackingStates.fulfilled, (state, action) => {
      if (action?.payload?.data) state.trackingStates = action?.payload?.data;
    });
    builder.addCase(fetchProducts.pending, (state, action) => {
      state.products.search = action.meta?.arg?.value || '';
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      if (action?.payload?.data?.table?.records)
        state.products = {
          ...state.products,
          [action.meta?.arg?.value || '']: action?.payload?.data?.table?.records || [],
        };
    });
    builder.addCase(fetchInternalProducts.pending, (state, action) => {
      state.internalProducts.search = action.meta?.arg?.value || '';
    });

    builder.addCase(fetchInternalProducts.fulfilled, (state, action) => {
      if (action?.payload?.data?.table?.records)
        state.internalProducts = {
          ...state.internalProducts,
          [action.meta?.arg?.value || '']: action?.payload?.data?.table?.records || [],
        };
    });
    builder.addCase(fetchTicketStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.ticketStatuses = action?.payload?.data;
    });
    builder.addCase(fetchAppointmentStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.appointmentStatuses = action?.payload?.data;
    });
    builder.addCase(fetchFollowUpStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.followUpStatuses = action?.payload?.data;
    });
    builder.addCase(fetchProductStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.productStatuses = action?.payload?.data;
    });
    builder.addCase(fetchProductVariantStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.productVariantStatuses = action?.payload?.data;
    });
    builder.addCase(fetchQuizAttemptStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.quizAttemptStatuses = action?.payload?.data;
    });
    builder.addCase(fetchTicketTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.ticketTypes = action?.payload?.data;
    });
    builder.addCase(fetchOrderTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.orderTypes = action?.payload?.data;
    });
    builder.addCase(fetchVoucherTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.voucherTypes = action?.payload?.data;
    });
    builder.addCase(fetchContactTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.contactTypes = action?.payload?.data;
    });

    builder.addCase(fetchCaseCommunicationTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.caseCommunicationTypes = action?.payload?.data;
    });
    builder.addCase(fetchAppointmentsTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.appointmentsTypes = action?.payload?.data;
    });
    builder.addCase(fetchProductTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.productTypes = action?.payload?.data;
    });
    builder.addCase(fetchLabOrderTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.labOrderTypes = action?.payload?.data;
    });
    builder.addCase(fetchDiscountUnitTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.discountUnitTypes = action?.payload?.data;
    });
    builder.addCase(fetchCouponTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.couponTypes = action?.payload?.data;
    });
    builder.addCase(fetchLeaveTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.leaveTypes = action?.payload?.data;
    });
    builder.addCase(fetchPharmacyTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.pharmacyTypes = action?.payload?.data;
    });
    builder.addCase(fetchTrackingCarriers.fulfilled, (state, action) => {
      if (action?.payload?.data) state.trackingCarriers = action?.payload?.data;
    });
    builder.addCase(fetchProcessors.fulfilled, (state, action) => {
      if (action?.payload?.data) state.processors = action?.payload?.data;
    });
    builder.addCase(fetchStates.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.states = formatOptionsForSelect({
          list: action?.payload?.data?.map((d) => ({ id: d?.[0], name: d?.[0], slug: d?.[1] })) || [],
          useSlug: true,
        });
    });
    builder.addCase(fetchAmericanStates.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.americanStates = formatOptionsForSelect({
          list:
            action?.payload?.data?.map((d) => ({
              ...d,
              name: `${d?.abbreviation} - ${d?.name} `,
              slug: d?.abbreviation,
            })) || [],
          useSlug: true,
        });
    });
    builder.addCase(fetchUserStates.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.states = formatOptionsForSelect({
          list: action?.payload?.data?.map((d) => ({ id: d?.[0], name: d?.[0], slug: d?.[1] })) || [],
          useSlug: true,
        });
    });
    builder.addCase(fetchJourneyStages.fulfilled, (state, action) => {
      if (action?.payload?.data) state.journeyStages = action?.payload?.data;
    });
    builder.addCase(fetchPrescriptions.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.prescriptions = formatOptionsForSelect({ list: action?.payload?.data?.map((d) => d?.name) || [] });
    });

    builder.addCase(fetchPharmacies.pending, (state, action) => {
      state.pharmacies.search = action.meta?.arg?.value || '';
    });
    builder.addCase(fetchPharmacies.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.pharmacies = {
          ...state.pharmacies,
          [action.meta?.arg?.value || '']: action?.payload?.data || [],
        };
    });
    builder.addCase(fetchChargeStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.chargeStatuses = action?.payload?.data;
    });
    builder.addCase(fetchIntakeStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.intakeStatuses = action?.payload?.data;
    });
    builder.addCase(fetchScheduledJobsStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.scheduledJobStatuses = action?.payload?.data;
    });
    builder.addCase(fetchLabOrderStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.labOrderStatuses = action?.payload?.data;
    });
    builder.addCase(fetchAdminUsers.fulfilled, (state, action) => {
      if (action?.payload?.data) state.adminUsers = action?.payload?.data;
    });
    builder.addCase(fetchThirdPartyUsers.fulfilled, (state, action) => {
      if (action?.payload?.data) state.thirdPartyUsers = action?.payload?.data;
    });
    builder.addCase(fetchPlanTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.planTypes = action?.payload?.data;
    });
    builder.addCase(fetchCategoryTypes.fulfilled, (state, action) => {
      if (action?.payload?.data) state.productCategories = action?.payload?.data;
    });
    builder.addCase(fetchRecommendedProductVariants.fulfilled, (state, action) => {
      if (action?.payload?.data) state.recommendedProductVariants = action?.payload?.data;
    });
    builder.addCase(fetchFollowupTemplates.fulfilled, (state, action) => {
      if (action?.payload?.data)
        state.followupTemplates =
          action?.payload?.data?.map((obj) => {
            return {
              name: obj?.action,
              label: obj?.action,
              ...obj,
            };
          }) || [];
    });
    builder.addCase(fetchAgentSalesStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.agentSalesStatuses = action?.payload?.data;
    });
    builder.addCase(fetchCreditStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.creditStatuses = action?.payload?.data;
    });
    builder.addCase(fetchLabReviewLabels.fulfilled, (state, action) => {
      if (action?.payload?.data) state.labReviewLabels = action?.payload?.data;
    });
    builder.addCase(fetchLabReviewIndexStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.labReviewIndexStatuses = action?.payload?.data;
    });
    builder.addCase(fetchLabReviewStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.labReviewStatuses = action?.payload?.data;
    });
    builder.addCase(fetchUserLabReviewStatuses.fulfilled, (state, action) => {
      if (action?.payload?.data) state.userLabReviewStatuses = action?.payload?.data;
    });
    builder.addCase(fetchQuizSteps.fulfilled, (state, action) => {
      if (action?.payload?.quizzes_details) {
        state.quizNames = formatOptionsForSelect({
          list: action?.payload?.quizzes_details?.map((quiz) => ({ ...quiz, id: quiz?.slug })),
        });
        state.quizSteps = formatOptionsForSelect({
          list: [
            ...new Set(
              action?.payload?.quizzes_details?.flatMap((quiz) => quiz?.skeleton?.map((s) => s?.step_name) || []),
            ),
          ],
        });
      }
    });
  },
});

export const { setProductCategories } = FiltersDataSlice.actions;
export default FiltersDataSlice.reducer;
